import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles, Leaf, MapPin } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { API } from "@/lib/api";
import { useAssistant } from "@/context/AssistantContext";
import { ASSISTANTS, getAssistant } from "@/constants/assistants";

const ICONS = { sparkles: Sparkles, leaf: Leaf, map: MapPin };

const greeting = (a) =>
  `Namaste — I'm the ${a.name}. ${a.desc} How can I help?`;

export default function ChatWidget() {
  const { isOpen, agent, setAgent, toggle } = useAssistant();
  const active = getAssistant(agent);
  const [messages, setMessages] = useState([{ role: "assistant", text: greeting(active) }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef("sess-" + Math.random().toString(36).slice(2));
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  // reset conversation when the agent changes
  useEffect(() => {
    setMessages([{ role: "assistant", text: greeting(getAssistant(agent)) }]);
    sessionId.current = "sess-" + Math.random().toString(36).slice(2);
  }, [agent]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg, agent }),
      });
      if (!res.ok || !res.body) throw new Error("chat request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + chunk };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Sorry, I couldn't respond right now. Please try again." };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  const ActiveIcon = ICONS[active.icon] || Sparkles;

  return (
    <>
      <button
        data-testid="chat-toggle"
        onClick={toggle}
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-brand-sand shadow-lg shadow-brand/30 transition-transform hover:scale-105 active:scale-95"
        aria-label="Ask Ayurvedam"
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {isOpen && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-5 z-50 flex h-[74vh] max-h-[600px] w-[min(92vw,400px)] flex-col overflow-hidden rounded-2xl border border-brand-line bg-brand-sand shadow-2xl"
        >
          <div className="bg-brand px-4 py-3.5 text-brand-sand">
            <div className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-sand/15">
                <ActiveIcon className="h-4 w-4" />
              </span>
              <div>
                <p className="font-heading text-base leading-none">{active.name}</p>
                <p className="text-[11px] text-brand-sand/70">AI · reviewed sources only</p>
              </div>
            </div>
            <div className="mt-3 flex gap-1.5">
              {ASSISTANTS.map((a) => {
                const Icon = ICONS[a.icon] || Sparkles;
                return (
                  <button
                    key={a.id}
                    data-testid={`agent-tab-${a.id}`}
                    onClick={() => setAgent(a.id)}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                      agent === a.id ? "bg-brand-sand text-brand" : "bg-brand-sand/10 text-brand-sand/80 hover:bg-brand-sand/20"
                    }`}
                  >
                    <Icon className="h-3 w-3" /> {a.name.split(" ")[0]}
                  </button>
                );
              })}
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}`}
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-brand text-brand-sand rounded-br-sm whitespace-pre-wrap"
                    : "mr-auto bg-white text-brand-ink border border-brand-line rounded-bl-sm"
                }`}
              >
                {m.role === "assistant" ? (
                  m.text ? (
                    <div className="chat-markdown space-y-2"><ReactMarkdown>{m.text}</ReactMarkdown></div>
                  ) : loading && i === messages.length - 1 ? "…" : ""
                ) : (
                  m.text
                )}
              </div>
            ))}
            {messages.length <= 1 && (
              <div className="space-y-2 pt-2">
                {active.suggestions.map((s) => (
                  <button
                    key={s}
                    data-testid="chat-suggestion"
                    onClick={() => send(s)}
                    className="block w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-left text-sm text-brand-ink transition-colors hover:border-brand hover:bg-brand/5"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 border-t border-brand-line bg-white p-3"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Ask the ${active.name}…`}
              className="flex-1 rounded-full border border-brand-line bg-brand-mist/50 px-4 py-2.5 text-sm outline-none focus:border-brand"
            />
            <button data-testid="chat-send" type="submit" disabled={loading} className="grid h-10 w-10 place-items-center rounded-full bg-brand text-brand-sand disabled:opacity-50">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
